import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";
import { Plus, Search, Trash2, Pencil, Download, Upload, FileDown, CheckSquare, Square, TrendingUp, FileText, Layers } from "lucide-react";
import EmployeeDialog from "@/components/EmployeeDialog";
import { useAuth } from "@/context/AuthContext";
import { hasRole, isRestricted } from "@/lib/roles";

export default function Employees() {
    const { confirm } = useConfirm();
    const { user } = useAuth();
    const isAdmin = hasRole(user, "admin");
    const clerk = isRestricted(user);
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [deptFilter, setDeptFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [incrementDialogOpen, setIncrementDialogOpen] = useState(false);
    const [excelDialogOpen, setExcelDialogOpen] = useState(false);
    const importRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (location.state?.openAdd) {
            setEditing(null);
            setDialogOpen(true);
            // clear state so refresh doesn't reopen
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [e, d] = await Promise.all([
                api.get("/employees", { params: { search: q || undefined, department: deptFilter, status: statusFilter } }),
                api.get("/departments"),
            ]);
            setEmployees(e.data);
            setDepartments(d.data);
        } catch (err) {
            toast.error("Failed to load employees");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deptFilter, statusFilter]);

    useEffect(() => {
        const t = setTimeout(() => load(), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const onDelete = async (emp) => {
        const ok = await confirm({
            title: `Delete ${emp.first_name} ${emp.last_name}?`,
            description: "This will permanently remove the employee record, their documents, payroll history, and link to the user account. This cannot be undone.",
            confirmLabel: "Delete employee",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/employees/${emp.id}`);
            toast.success("Employee deleted");
            load();
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const toggleSelect = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === employees.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(employees.map((e) => e.id)));
    };

    const onBulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        const ok = await confirm({
            title: `Delete ${ids.length} employee${ids.length === 1 ? "" : "s"}?`,
            description: "Permanent removal of all selected employees, their documents, payroll history, and linked accounts. Cannot be undone.",
            confirmLabel: `Delete ${ids.length}`,
            danger: true,
        });
        if (!ok) return;
        setBulkDeleting(true);
        try {
            const { data } = await api.post("/employees/bulk-delete", { employee_ids: ids });
            toast.success(`Deleted ${data.deleted_count} employee${data.deleted_count === 1 ? "" : "s"}`);
            setSelectedIds(new Set());
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
        } finally {
            setBulkDeleting(false);
        }
    };

    const onExport = async () => {
        setExporting(true);
        try {
            const res = await api.get("/employees/export", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.download = `employees_${ts}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Export downloaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    const onDownloadTemplate = async () => {
        try {
            const res = await api.get("/employees/import-template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "employee_import_template.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Template download failed");
        }
    };

    const onImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset so same file can be re-picked
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Please select an .xlsx file");
            return;
        }
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/employees/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const parts = [];
            if (data.created) parts.push(`${data.created} created`);
            if (data.updated) parts.push(`${data.updated} updated`);
            if (data.total_errors) parts.push(`${data.total_errors} errors`);
            toast.success(`Import complete — ${parts.join(", ") || "nothing changed"}`);
            if (data.errors?.length) {
                console.warn("Import errors:", data.errors);
            }
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    const filtered = employees; // filtering done server-side

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="employees-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Directory</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Employees
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-lg">
                        Every person in the organization. Add new hires, edit their profile, and
                        keep their info current.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        ref={importRef}
                        type="file"
                        accept=".xlsx"
                        hidden
                        onChange={onImportFile}
                        data-testid="employee-import-file-input"
                    />
                    {!clerk && (<>
                    <button
                        onClick={onDownloadTemplate}
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink hover:text-white transition-colors"
                        data-testid="employee-import-template-button"
                        title="Download blank Excel template with correct column headers"
                    >
                        <FileDown className="w-4 h-4" />
                        Template
                    </button>
                    <button
                        onClick={() => importRef.current?.click()}
                        disabled={importing}
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink hover:text-white transition-colors disabled:opacity-60"
                        data-testid="employee-import-button"
                    >
                        <Upload className="w-4 h-4" />
                        {importing ? "Importing…" : "Import Excel"}
                    </button>
                    <button
                        onClick={onExport}
                        disabled={exporting}
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink hover:text-white transition-colors disabled:opacity-60"
                        data-testid="employee-export-button"
                    >
                        <Download className="w-4 h-4" />
                        {exporting ? "Preparing…" : "Export Excel"}
                    </button>
                    <Link
                        to="/bulk-divisions"
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink hover:text-white transition-colors"
                        data-testid="bulk-divisions-button"
                    >
                        <Layers className="w-4 h-4" /> Set divisions
                    </Link>
                    </>)}
                    <button
                        onClick={() => { setEditing(null); setDialogOpen(true); }}
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90 transition-colors"
                        data-testid="add-employee-button"
                    >
                        <Plus className="w-4 h-4" /> Add employee
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 bg-white border border-ink/10 p-4">
                <div className="md:col-span-6 flex items-center gap-3 border border-ink/10 px-3 py-2.5">
                    <Search className="w-4 h-4 text-ink/40" />
                    <input
                        placeholder="Search name, staff no, department, division, phone…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full bg-transparent outline-none text-sm text-ink placeholder:text-ink/40"
                        data-testid="employee-search-input"
                    />
                </div>
                <select
                    value={deptFilter}
                    onChange={(e) => setDeptFilter(e.target.value)}
                    className="md:col-span-3 border border-ink/10 bg-white px-3 py-2.5 text-sm"
                    data-testid="employee-dept-filter"
                >
                    <option value="all">All departments</option>
                    {departments.map((d) => (
                        <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                </select>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="md:col-span-3 border border-ink/10 bg-white px-3 py-2.5 text-sm"
                    data-testid="employee-status-filter"
                >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="on_leave">On leave</option>
                    <option value="resigned">Resigned</option>
                    <option value="terminated">Terminated</option>
                </select>
            </div>

            {/* Bulk action bar */}
            {selectedIds.size > 0 && (
                <div className="mt-4 px-4 py-3 bg-ink text-white flex items-center justify-between flex-wrap gap-3 rounded" data-testid="bulk-action-bar">
                    <div className="text-sm font-medium">
                        <span className="tabular-nums" data-testid="bulk-selected-count">{selectedIds.size}</span> selected
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            className="text-xs uppercase tracking-[0.15em] text-white/70 hover:text-white"
                            data-testid="bulk-clear"
                        >
                            Clear
                        </button>
                        {isAdmin && (
                        <button
                            onClick={() => setIncrementDialogOpen(true)}
                            className="inline-flex items-center gap-1.5 bg-klein hover:bg-klein/90 px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium rounded"
                            data-testid="bulk-increment-btn"
                        >
                            <TrendingUp className="w-4 h-4" />
                            Increment {selectedIds.size}
                        </button>
                        )}
                        <button
                            onClick={onBulkDelete}
                            disabled={bulkDeleting}
                            className="inline-flex items-center gap-1.5 bg-destructive hover:bg-destructive/90 disabled:opacity-50 px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium rounded"
                            data-testid="bulk-delete-btn"
                        >
                            <Trash2 className="w-4 h-4" />
                            {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
                        </button>
                    </div>
                </div>
            )}

            {/* Excel template entry (always visible to admin) */}
            {isAdmin && (
            <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="bulk-tools">
                <button
                    onClick={() => setExcelDialogOpen(true)}
                    className="inline-flex items-center gap-2 border border-ink/20 px-3 py-2 text-xs uppercase tracking-[0.12em] text-ink/70 hover:bg-ink/5 rounded"
                    data-testid="bulk-excel-btn"
                >
                    <FileText className="w-3.5 h-3.5" />
                    Salary increment via Excel
                </button>
                <span className="text-[11px] text-ink/40">Or select employees above for inline percent/fixed increment</span>
            </div>
            )}

            {/* Table */}
            <div className="mt-6 bg-white border border-ink/10 overflow-auto max-h-[420px]">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-ink/20">
                            <th className="px-4 py-4 w-10">
                                <button
                                    onClick={toggleSelectAll}
                                    className="text-ink/60 hover:text-klein"
                                    title={selectedIds.size === employees.length ? "Deselect all" : "Select all"}
                                    data-testid="bulk-select-all"
                                >
                                    {selectedIds.size > 0 && selectedIds.size === employees.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                </button>
                            </th>
                            <th className="text-left overline px-6 py-4">Employee</th>
                            <th className="text-left overline px-6 py-4">Position</th>
                            <th className="text-left overline px-6 py-4 hidden md:table-cell">Department</th>
                            <th className="text-left overline px-6 py-4 hidden xl:table-cell">Category</th>
                            <th className="text-left overline px-6 py-4 hidden lg:table-cell">Joined</th>
                            <th className="text-left overline px-6 py-4">Status</th>
                            <th className="text-right overline px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="8" className="px-6 py-20 text-center text-sm text-ink/40">
                                    Loading employees…
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="px-6 py-20 text-center">
                                    <div className="text-ink/40 text-sm mb-3">No employees match your filters.</div>
                                    <button
                                        onClick={() => { setEditing(null); setDialogOpen(true); }}
                                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2 text-sm hover:bg-ink hover:text-white"
                                        data-testid="empty-add-employee-button"
                                    >
                                        <Plus className="w-4 h-4" /> Add your first employee
                                    </button>
                                </td>
                            </tr>
                        ) : (
                            filtered.map((e) => (
                                <tr
                                    key={e.id}
                                    className={`border-b border-ink/10 hover:bg-bone/70 transition-colors ${selectedIds.has(e.id) ? "bg-klein/5" : ""}`}
                                    data-testid={`employee-row-${e.id}`}
                                >
                                    <td className="px-4 py-4">
                                        <button
                                            onClick={() => toggleSelect(e.id)}
                                            className="text-ink/60 hover:text-klein"
                                            data-testid={`employee-select-${e.id}`}
                                        >
                                            {selectedIds.has(e.id) ? <CheckSquare className="w-4 h-4 text-klein" /> : <Square className="w-4 h-4" />}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Link
                                            to={`/employees/${e.id}`}
                                            className="flex items-center gap-3 group"
                                            data-testid={`employee-view-${e.id}`}
                                        >
                                            <div className="w-10 h-10 bg-ink/10 overflow-hidden shrink-0">
                                                {e.photo ? (
                                                    <img src={e.photo} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center font-display font-bold text-ink/60 text-sm">
                                                        {(e.first_name?.[0] || "") + (e.last_name?.[0] || "")}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-ink group-hover:text-klein truncate">
                                                    {e.first_name} {e.last_name}
                                                </div>
                                                <div className="text-xs text-ink/50 truncate">{e.email}</div>
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-ink/80">{e.position || "—"}</td>
                                    <td className="px-6 py-4 text-sm text-ink/80 hidden md:table-cell">{e.department || "—"}</td>
                                    <td className="px-6 py-4 text-sm text-ink/80 hidden xl:table-cell">
                                        {e.category ? (
                                            <span className="inline-flex items-center px-2 py-1 border border-ink/15 text-[11px] uppercase tracking-[0.15em] text-ink/70">
                                                {e.category}
                                            </span>
                                        ) : "—"}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-ink/80 hidden lg:table-cell">{e.joining_date || "—"}</td>
                                    <td className="px-6 py-4">
                                        <StatusPill status={e.status} />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => { setEditing(e); setDialogOpen(true); }}
                                                className="p-2 text-ink/60 hover:text-klein hover:bg-klein/5"
                                                title="Edit"
                                                data-testid={`employee-edit-${e.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            {!clerk && <button
                                                onClick={() => onDelete(e)}
                                                className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5"
                                                title="Delete"
                                                data-testid={`employee-delete-${e.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <EmployeeDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={load}
                employee={editing}
                departments={departments}
            />
            {incrementDialogOpen && (
                <BulkIncrementDialog
                    employeeIds={Array.from(selectedIds)}
                    onClose={() => setIncrementDialogOpen(false)}
                    onDone={() => { setSelectedIds(new Set()); load(); }}
                />
            )}
            {excelDialogOpen && (
                <ExcelIncrementDialog
                    onClose={() => setExcelDialogOpen(false)}
                    onDone={load}
                />
            )}
        </div>
    );
}

function BulkIncrementDialog({ employeeIds, onClose, onDone }) {
    const { confirm } = useConfirm();
    const [mode, setMode] = useState("percent");
    const [value, setValue] = useState("");
    const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Mode-specific copy in one place — no nested ternaries in JSX
    const MODE_COPY = {
        percent: { label: "Percent change (e.g. 10 for +10%, -5 for -5%)", placeholder: "10" },
        fixed:   { label: "Amount to add (₦)",                              placeholder: "50000" },
        set_to:  { label: "Absolute new salary (₦)",                        placeholder: "350000" },
    };
    const copy = MODE_COPY[mode] || MODE_COPY.percent;

    const onSubmit = async (e) => {
        e.preventDefault();
        const v = Number(value);
        if (!Number.isFinite(v) || (mode !== "set_to" && v === 0)) {
            toast.error("Enter a non-zero value");
            return;
        }
        if (mode === "set_to" && v <= 0) {
            toast.error("New salary must be > 0");
            return;
        }
        const labels = { percent: `+${v}%`, fixed: `+₦${v.toLocaleString()}`, set_to: `Set all to ₦${v.toLocaleString()}` };
        const ok = await confirm({
            title: `Apply "${labels[mode]}" to ${employeeIds.length} employee${employeeIds.length === 1 ? "" : "s"}?`,
            description: "A salary_history entry will be written so each change is auditable. Cannot be undone in bulk — individual records can be edited afterwards.",
            confirmLabel: "Apply increase",
        });
        if (!ok) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/employees/bulk-salary-increment", {
                employee_ids: employeeIds,
                mode,
                value: v,
                effective_date: effectiveDate,
                reason: reason || `Bulk ${mode} ${v}`,
            });
            const sign = data.total_difference >= 0 ? "+" : "−";
            toast.success(`Updated ${data.affected_count} employees · total ${sign}₦${Math.abs(data.total_difference).toLocaleString()}/month`);
            if (data.skipped_count > 0) {
                toast(`${data.skipped_count} skipped (e.g. no existing salary)`, { duration: 5000 });
            }
            onDone();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Increment failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="bulk-increment-dialog">
            <div className="bg-white max-w-md w-full rounded-lg shadow-2xl">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <h2 className="font-display font-bold text-lg flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-klein" /> Bulk salary increment
                    </h2>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink" data-testid="inc-dialog-close">✕</button>
                </div>
                <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
                    <div className="text-xs text-ink/60">Applying to <strong className="text-ink">{employeeIds.length}</strong> selected employee{employeeIds.length === 1 ? "" : "s"}.</div>
                    <div>
                        <label className="overline">Mode</label>
                        <div className="mt-2 inline-flex border border-ink/15 rounded overflow-hidden">
                            <ModeButton current={mode} value="percent" onClick={setMode}>Percent</ModeButton>
                            <ModeButton current={mode} value="fixed" onClick={setMode}>Fixed ₦</ModeButton>
                            <ModeButton current={mode} value="set_to" onClick={setMode}>Set to</ModeButton>
                        </div>
                    </div>
                    <div>
                        <label className="overline">
                            {copy.label}
                        </label>
                        <input
                            type="number"
                            step="any"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={copy.placeholder}
                            className="mt-2 w-full border border-ink/15 px-3 py-2.5 text-sm rounded focus:border-klein focus:outline-none"
                            data-testid="inc-value"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="overline">Effective date</label>
                        <input
                            type="date"
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.target.value)}
                            className="mt-2 w-full border border-ink/15 px-3 py-2.5 text-sm rounded focus:border-klein focus:outline-none"
                            data-testid="inc-effective-date"
                        />
                    </div>
                    <div>
                        <label className="overline">Reason (logged to salary history)</label>
                        <input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Annual review 2026"
                            className="mt-2 w-full border border-ink/15 px-3 py-2.5 text-sm rounded focus:border-klein focus:outline-none"
                            data-testid="inc-reason"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-ink/60 hover:text-ink" data-testid="inc-cancel">Cancel</button>
                        <button type="submit" disabled={submitting || !value} className="inline-flex items-center gap-1.5 bg-klein text-white px-4 py-2 text-sm rounded hover:bg-klein/90 disabled:opacity-50" data-testid="inc-submit">
                            {submitting ? "Applying…" : "Apply"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ModeButton({ current, value, onClick, children }) {
    return (
        <button
            type="button"
            onClick={() => onClick(value)}
            className={`px-3 py-2 text-xs uppercase tracking-[0.12em] ${current === value ? "bg-klein text-white" : "bg-white text-ink hover:bg-ink/5"}`}
            data-testid={`inc-mode-${value}`}
        >
            {children}
        </button>
    );
}

function ExcelIncrementDialog({ onClose, onDone }) {
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const onDownload = async () => {
        setDownloading(true);
        try {
            const { data } = await api.get("/bulk/salary-increment-template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "salary_increment_template.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded. Fill new_salary column and re-upload.");
        } catch (e) {
            toast.error("Could not download template");
        } finally {
            setDownloading(false);
        }
    };

    const onPick = () => fileRef.current?.click();
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Please upload an .xlsx file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/bulk/salary-increment/import?effective_date=${encodeURIComponent(effectiveDate)}`, fd);
            setResult(data);
            const sign = data.total_difference >= 0 ? "+" : "−";
            toast.success(`Updated ${data.affected_count} employees · ${sign}₦${Math.abs(data.total_difference).toLocaleString()}/month`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="excel-increment-dialog">
            <div className="bg-white max-w-xl w-full rounded-lg shadow-2xl">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <h2 className="font-display font-bold text-lg flex items-center gap-2">
                        <FileText className="w-4 h-4 text-klein" /> Salary increment via Excel
                    </h2>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink" data-testid="excel-dialog-close">✕</button>
                </div>
                <div className="px-6 py-5 space-y-5">
                    <ol className="text-sm text-ink/70 space-y-2 list-decimal list-inside">
                        <li>Download the template — it's pre-filled with your active employees and current salaries.</li>
                        <li>Fill the <code className="bg-bone px-1 rounded">new_salary</code> column for the employees you want to update. Leave blank to skip an employee.</li>
                        <li>Optionally add a <code className="bg-bone px-1 rounded">reason</code> for the salary history log.</li>
                        <li>Upload the saved file below. Each change writes one salary_history entry.</li>
                    </ol>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={onDownload}
                            disabled={downloading}
                            className="inline-flex items-center justify-center gap-2 border border-klein text-klein px-4 py-3 text-sm rounded hover:bg-klein hover:text-white disabled:opacity-50"
                            data-testid="excel-download-btn"
                        >
                            <Download className="w-4 h-4" />
                            {downloading ? "Downloading…" : "1. Download template"}
                        </button>
                        <button
                            onClick={onPick}
                            disabled={uploading}
                            className="inline-flex items-center justify-center gap-2 bg-klein text-white px-4 py-3 text-sm rounded hover:bg-klein/90 disabled:opacity-50"
                            data-testid="excel-upload-btn"
                        >
                            <Upload className="w-4 h-4" />
                            {uploading ? "Uploading…" : "2. Upload filled file"}
                        </button>
                        <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onUpload} data-testid="excel-file-input" />
                    </div>

                    <div>
                        <label className="overline">Effective date</label>
                        <input
                            type="date"
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.target.value)}
                            className="mt-2 w-full border border-ink/15 px-3 py-2.5 text-sm rounded focus:border-klein focus:outline-none"
                            data-testid="excel-effective-date"
                        />
                    </div>

                    {result && (
                        <div className="border border-ink/10 rounded p-4 bg-bone/40 space-y-2 text-sm" data-testid="excel-result">
                            <div className="flex justify-between"><span className="text-ink/60">Affected</span><strong className="text-emerald-700">{result.affected_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Skipped</span><strong>{result.skipped_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Not found</span><strong>{result.not_found_count}</strong></div>
                            <div className="flex justify-between border-t border-ink/10 pt-2"><span className="text-ink/60">Total monthly impact</span><strong className="text-klein">{result.total_difference >= 0 ? "+" : ""}₦{Number(result.total_difference).toLocaleString()}</strong></div>
                            {result.skipped_count > 0 && result.skipped[0] && (
                                <div className="text-[11px] text-ink/50 mt-2">e.g. {result.skipped[0].reason}</div>
                            )}
                        </div>
                    )}

                    <div className="text-right pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-ink/60 hover:text-ink" data-testid="excel-close">Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        active: { label: "Active", cls: "bg-green-50 text-green-700 border-green-200" },
        on_leave: { label: "On leave", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
        resigned: { label: "Resigned", cls: "bg-orange-50 text-orange-700 border-orange-200" },
        terminated: { label: "Terminated", cls: "bg-red-50 text-red-700 border-red-200" },
    };
    const s = map[status] || { label: status || "—", cls: "bg-ink/5 text-ink/70 border-ink/10" };
    return (
        <span className={`inline-flex items-center px-2 py-1 border text-[11px] uppercase tracking-[0.15em] ${s.cls}`}>
            {s.label}
        </span>
    );
}
